export type AvanceeTimelineStep = {
  id: string;
  title: string;
  icon: 'assignment' | 'handshake' | 'build' | 'construction' | 'homeWork' | 'checkCircle';
  status: 'termine' | 'en_cours' | 'prevu' | 'en_attente' | 'a_venir';
  period: string;
};

export const VITRINE_AVANCEE_TIMELINE_ICONS = [
  { id: 'assignment', label: 'Étude / dossier' },
  { id: 'handshake', label: 'Permis / accord' },
  { id: 'build', label: 'Préparation' },
  { id: 'construction', label: 'Construction' },
  { id: 'homeWork', label: 'Finitions' },
  { id: 'checkCircle', label: 'Livraison' },
] as const;

export const VITRINE_AVANCEE_STEP_STATUSES = [
  { id: 'termine', label: 'Terminé' },
  { id: 'en_cours', label: 'En cours' },
  { id: 'prevu', label: 'Prévu' },
  { id: 'en_attente', label: 'En attente' },
  { id: 'a_venir', label: 'À venir' },
] as const;

const ICON_IDS = new Set(VITRINE_AVANCEE_TIMELINE_ICONS.map((i) => i.id));
const STATUS_IDS = new Set(VITRINE_AVANCEE_STEP_STATUSES.map((s) => s.id));

export function defaultAvanceeTimelineSteps(dateLivraison = '4ème trim. 2026'): AvanceeTimelineStep[] {
  return [
    { id: 'etude', title: 'Étude et conception', icon: 'assignment', status: 'termine', period: 'Q1 2023' },
    { id: 'permis', title: 'Permis et autorisations', icon: 'handshake', status: 'termine', period: 'Q2 2023' },
    { id: 'preparation', title: 'Préparation du terrain', icon: 'build', status: 'termine', period: 'Q3 2023' },
    { id: 'construction', title: 'Construction', icon: 'construction', status: 'en_cours', period: 'Q1 2024' },
    { id: 'finitions', title: 'Finitions', icon: 'homeWork', status: 'en_cours', period: 'Q3 2024' },
    { id: 'livraison', title: 'Livraison', icon: 'checkCircle', status: 'prevu', period: dateLivraison },
  ];
}

function normalizeStep(raw: Partial<AvanceeTimelineStep>, fallback: AvanceeTimelineStep): AvanceeTimelineStep {
  const icon = raw.icon && ICON_IDS.has(raw.icon) ? raw.icon : fallback.icon;
  const status = raw.status && STATUS_IDS.has(raw.status) ? raw.status : fallback.status;
  return {
    id: String(raw.id || fallback.id).trim() || fallback.id,
    title: String(raw.title || fallback.title).trim() || fallback.title,
    icon: icon as AvanceeTimelineStep['icon'],
    status: status as AvanceeTimelineStep['status'],
    period: String(raw.period ?? fallback.period ?? '').trim(),
  };
}

export function normalizeAvanceeTimelineSteps(
  raw: unknown,
  dateLivraison?: string,
): AvanceeTimelineStep[] {
  const defaults = defaultAvanceeTimelineSteps(dateLivraison);
  if (!Array.isArray(raw) || raw.length === 0) return defaults;

  const byId = new Map(defaults.map((d) => [d.id, d]));
  const out: AvanceeTimelineStep[] = [];

  raw.forEach((item, i) => {
    if (!item || typeof item !== 'object') return;
    const step = item as Partial<AvanceeTimelineStep>;
    const fallback = (step.id && byId.get(step.id)) || defaults[i] || defaults[defaults.length - 1];
    out.push(normalizeStep(step, fallback));
  });

  return out.length > 0 ? out : defaults;
}

export function avanceeStepStatusLabel(statusId: string): string {
  return VITRINE_AVANCEE_STEP_STATUSES.find((s) => s.id === statusId)?.label || statusId || '…';
}

export function isAvanceeStepActive(statusId: string): boolean {
  return statusId === 'termine' || statusId === 'en_cours';
}

/** 3 statuts affichés sur le bandeau sombre du catalogue (dérivés de la frise Avancée). */
export const CATALOG_AVANCEE_BANNER_LABELS = {
  etude: 'Étude & préparation',
  travaux: 'Travaux en cours',
  livre: 'Projet livré',
} as const;

export type CatalogAvanceeBannerPhase = keyof typeof CATALOG_AVANCEE_BANNER_LABELS;

type MacroPhase = 'etude' | 'travaux' | 'livraison';

function stepMacroPhase(step: AvanceeTimelineStep): MacroPhase {
  const id = String(step.id || '').toLowerCase();
  const title = String(step.title || '').toLowerCase();

  if (id.includes('livraison') || step.icon === 'checkCircle' || title.includes('livraison')) {
    return 'livraison';
  }
  if (
    id.includes('construction') ||
    id.includes('finition') ||
    step.icon === 'construction' ||
    step.icon === 'homeWork' ||
    title.includes('construction') ||
    title.includes('finition')
  ) {
    return 'travaux';
  }
  return 'etude';
}

/**
 * Phase catalogue (3 statuts) à partir de la frise « Avancée du projet ».
 */
export function resolveAvanceeCatalogBannerPhase(
  rawSteps: unknown,
  dateLivraison?: string,
): CatalogAvanceeBannerPhase {
  const steps = normalizeAvanceeTimelineSteps(rawSteps, dateLivraison);
  const livraisonStep = steps.find((s) => stepMacroPhase(s) === 'livraison');

  if (livraisonStep?.status === 'termine') return 'livre';
  if (steps.length > 0 && steps.every((s) => s.status === 'termine')) return 'livre';

  const enCours = steps.find((s) => s.status === 'en_cours');
  if (enCours) {
    const macro = stepMacroPhase(enCours);
    if (macro === 'livraison') return 'livre';
    if (macro === 'travaux') return 'travaux';
    return 'etude';
  }

  const etudeSteps = steps.filter((s) => stepMacroPhase(s) === 'etude');
  const travauxSteps = steps.filter((s) => stepMacroPhase(s) === 'travaux');

  if (travauxSteps.some((s) => s.status === 'en_cours')) return 'travaux';

  const etudeTerminee =
    etudeSteps.length === 0 || etudeSteps.every((s) => s.status === 'termine');
  const travauxDemarres = travauxSteps.some(
    (s) => s.status === 'termine' || s.status === 'en_cours' || s.status === 'prevu',
  );

  if (etudeTerminee && travauxDemarres) {
    return 'travaux';
  }

  return 'etude';
}

/** Libellé du bandeau sombre catalogue. */
export function resolveAvanceeCatalogBannerLabel(
  rawSteps: unknown,
  dateLivraison?: string,
): string {
  const phase = resolveAvanceeCatalogBannerPhase(rawSteps, dateLivraison);
  return CATALOG_AVANCEE_BANNER_LABELS[phase];
}

function normalizeCatalogStatusKey(label?: string | null): string {
  return String(label ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Ordre des cartes catalogue (accueil) :
 * 1 En commercialisation → 2 Travaux → 3 Étude & préparation → 4 Projet livré.
 */
export function catalogStatusDisplaySortRank(statusLabel?: string | null): number {
  const n = normalizeCatalogStatusKey(statusLabel);
  if (n.includes('commercialisation')) return 0;
  if (n.includes('travaux')) return 1;
  if (n.includes('etude') || n.includes('preparation')) return 2;
  if (n.includes('livre')) return 3;
  return 2;
}

export function compareCatalogCardsByStatus(
  aLabel?: string | null,
  bLabel?: string | null,
  aTitle?: string,
  bTitle?: string,
): number {
  const byStatus =
    catalogStatusDisplaySortRank(aLabel) - catalogStatusDisplaySortRank(bLabel);
  if (byStatus !== 0) return byStatus;
  return String(aTitle ?? '').localeCompare(String(bTitle ?? ''), 'fr', {
    sensitivity: 'base',
  });
}
